/**
 * API Error class for handling HTTP request failures with comprehensive error information.
 *
 * This class provides a standardized way to handle API errors across the application,
 * offering detailed error information including status codes, messages, and additional data.
 *
 * @example
 * ```typescript
 * // Basic usage
 * throw new ApiError(404, "User not found");
 *
 * // With additional error data
 * throw new ApiError(400, "Validation failed", { field: "email", code: "INVALID_FORMAT" });
 *
 * // Network error (no response)
 * throw new ApiError(500, "Network error");
 * ```
 */
export class ApiError extends Error {
  /**
   * HTTP status code associated with the error.
   * @readonly
   */
  public readonly status: number;

  /**
   * Additional error data from the server response.
   * This can contain validation errors, error codes, or other contextual information.
   * @readonly
   */
  public readonly data?: unknown;

  /**
   * Whether this error was caused by a network failure (no response received).
   * @readonly
   */
  public readonly isNetworkError: boolean;

  /**
   * Whether this error represents a client-side error (4xx status codes).
   * @readonly
   */
  public readonly isClientError: boolean;

  /**
   * Whether this error represents a server-side error (5xx status codes).
   * @readonly
   */
  public readonly isServerError: boolean;

  /**
   * Human-readable error message for display to users.
   * @readonly
   */
  public readonly userMessage: string;

  /**
   * Creates a new ApiError instance.
   *
   * @param status - HTTP status code (e.g., 404, 500)
   * @param message - Error message (defaults to status text if not provided)
   * @param data - Additional error data from server response
   */
  constructor(status: number, message?: string, data?: unknown) {
    // Ensure we have a meaningful message
    const errorMessage = message || ApiError.getDefaultMessage(status);

    super(errorMessage);

    // Set the name property to distinguish from generic Error instances
    this.name = "ApiError";

    // Ensure proper prototype chain for instanceof checks
    Object.setPrototypeOf(this, ApiError.prototype);

    this.status = status;
    this.data = data;
    this.isNetworkError = status === 0 || status >= 500;
    this.isClientError = status >= 400 && status < 500;
    this.isServerError = status >= 500;
    this.userMessage = this.getUserFriendlyMessage();
  }

  /**
   * Creates an ApiError from a fetch Response object.
   * This is a convenience method for handling fetch API responses.
   *
   * @param response - The fetch Response object
   * @returns Promise that resolves to an ApiError instance
   */
  static async fromResponse(response: Response): Promise<ApiError> {
    let errorData: unknown;

    try {
      // Attempt to parse error data from response
      const contentType = response.headers.get("content-type");
      if (contentType?.includes("application/json")) {
        errorData = await response.json();
      } else {
        errorData = await response.text();
      }
    } catch {
      // If parsing fails, we'll use the status text
      errorData = undefined;
    }

    // Extract message from error data if available
    let message: string | undefined;
    if (typeof errorData === "object" && errorData !== null) {
      const errorObj = errorData as Record<string, unknown>;
      if (typeof errorObj.message === "string") {
        message = errorObj.message;
      } else if (typeof errorObj.error === "string") {
        message = errorObj.error;
      }
    } else if (typeof errorData === "string") {
      message = errorData;
    }

    return new ApiError(response.status, message, errorData);
  }

  /**
   * Creates an ApiError for network failures (when no response is received).
   *
   * @param message - Optional custom message (defaults to network error message)
   * @returns ApiError instance representing a network failure
   */
  static networkError(message?: string): ApiError {
    return new ApiError(0, message || "Network error occurred");
  }

  /**
   * Returns a default error message for a given HTTP status code.
   * Used when no specific message is provided to the constructor.
   *
   * @param status - HTTP status code
   * @returns Default error message for the status code
   */
  private static getDefaultMessage(status: number): string {
    const statusMessages: Record<number, string> = {
      400: "Bad Request",
      401: "Unauthorized",
      403: "Forbidden",
      404: "Not Found",
      405: "Method Not Allowed",
      409: "Conflict",
      422: "Unprocessable Entity",
      429: "Too Many Requests",
      500: "Internal Server Error",
      502: "Bad Gateway",
      503: "Service Unavailable",
      504: "Gateway Timeout",
    };

    return statusMessages[status] || `HTTP Error ${status}`;
  }

  /**
   * Serializes the error to a plain object for logging or transmission.
   * Useful for debugging and error reporting.
   *
   * @returns Plain object representation of the error
   */
  toJSON(): Record<string, unknown> {
    return {
      name: this.name,
      message: this.message,
      status: this.status,
      data: this.data,
      isNetworkError: this.isNetworkError,
      isClientError: this.isClientError,
      isServerError: this.isServerError,
      userMessage: this.userMessage,
      stack: this.stack,
    };
  }

  /**
   * Returns a string representation of the error.
   * Includes status code and message for easy debugging.
   *
   * @returns String representation of the error
   */
  toString(): string {
    return `ApiError ${this.status}: ${this.message}`;
  }

  /**
   * Returns a user-friendly error message based on the status code and context.
   * This method prioritizes server-provided messages over generic status code messages.
   *
   * @returns A human-readable error message suitable for display to end users
   */
  private getUserFriendlyMessage(): string {
    // If we have a server-provided message that's not just a generic status text,
    // prioritize it over generic status code messages
    if (this.message && this.message !== this.getStatusText(this.status)) {
      return this.message;
    }

    // Handle specific status codes with user-friendly messages
    switch (this.status) {
      case 400:
        return "Invalid request. Please check your settings and try again.";
      case 401:
        return "You need to sign in to access this resource.";
      case 403:
        return "You do not have permission to perform this action.";
      case 404:
        return "The requested resource was not found.";
      case 409:
        return "This action conflicts with the current state. Please refresh and try again.";
      case 422:
        return "The provided data is invalid. Please check your settings.";
      case 429:
        return "Too many requests. Please wait a moment and try again.";
      case 500:
        return "Something went wrong on our end. Please try again later.";
      case 502:
      case 503:
      case 504:
        return "The service is temporarily unavailable. Please try again later.";
      default:
        // For network errors or unknown status codes, provide a generic message
        if (this.isNetworkError) {
          return "Unable to connect to the server. Please check your internet connection.";
        }
        return this.message || "An unexpected error occurred.";
    }
  }

  /**
   * Returns the standard HTTP status text for a given status code.
   * Used to detect if the message is just a generic status text.
   */
  private getStatusText(status: number): string {
    const statusTexts: Record<number, string> = {
      400: "Bad Request",
      401: "Unauthorized",
      403: "Forbidden",
      404: "Not Found",
      409: "Conflict",
      422: "Unprocessable Entity",
      429: "Too Many Requests",
      500: "Internal Server Error",
      502: "Bad Gateway",
      503: "Service Unavailable",
      504: "Gateway Timeout",
    };
    return statusTexts[status] || "Unknown Error";
  }
}

/**
 * Type guard to check if an unknown value is an ApiError instance.
 *
 * @param error - The value to check
 * @returns True if the value is an ApiError instance
 *
 * @example
 * ```typescript
 * try {
 *   await someApiCall();
 * } catch (error) {
 *   if (isApiError(error)) {
 *     console.log('API Error:', error.status, error.userMessage);
 *   }
 * }
 * ```
 */
export const isApiError = (error: unknown): error is ApiError => {
  return error instanceof ApiError;
}

/**
 * Type guard to check if an unknown value is an ApiError with a specific status code.
 *
 * @param error - The value to check
 * @param status - The expected status code
 * @returns True if the value is an ApiError with the specified status code
 *
 * @example
 * ```typescript
 * if (isApiErrorWithStatus(error, 404)) {
 *   console.log('Resource not found');
 * }
 * ```
 */
export const isApiErrorWithStatus = (error: unknown, status: number,): error is ApiError => {
  return isApiError(error) && error.status === status;
}

/**
 * Type guard to check if an unknown value is a network error (no response received).
 *
 * @param error - The value to check
 * @returns True if the value is an ApiError representing a network failure
 *
 * @example
 * ```typescript
 * if (isNetworkError(error)) {
 *   console.log('Network connectivity issue');
 * }
 * ```
 */
export const isNetworkError = (error: unknown): error is ApiError => {
  return isApiError(error) && error.isNetworkError;
}
